import { minorUnitDigits, toMajor } from "./minor-units";
import type { Money } from "./types";

export const DEFAULT_LOCALE = "en-IN";

export type FormatMoneyOptions = {
  /** BCP 47 tag. `en-IN` groups rupees as lakh and crore: ₹1,24,300. */
  readonly locale?: string;
  /** `auto` drops ".00" on whole amounts; `always` and `never` force it on or off. */
  readonly fraction?: "auto" | "always" | "never";
};

function fractionDigits(money: Money, fraction: FormatMoneyOptions["fraction"]): number {
  const full = minorUnitDigits(money.currency);
  if (fraction === "always") return full;
  if (fraction === "never") return 0;
  return money.amountMinor % 10 ** full === 0 ? 0 : full;
}

/** "₹1,24,300" or "₹1,243.50": the console does all money formatting. */
export function formatMoney(money: Money, options: FormatMoneyOptions = {}): string {
  const digits = fractionDigits(money, options.fraction);
  return new Intl.NumberFormat(options.locale ?? DEFAULT_LOCALE, {
    style: "currency",
    currency: money.currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(toMajor(money));
}

/** "₹1.2L" or "₹3.4Cr": a short form for chart axes and tight tiles. */
export function formatMoneyCompact(money: Money, locale: string = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: money.currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(toMajor(money));
}

/** The sign a currency is written with, "₹", "$", "€" or "CHF": what a money box shows before the number. */
export function currencySymbol(currency: string, locale: string = DEFAULT_LOCALE): string {
  const part = new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" })
    .formatToParts(0)
    .find((piece) => piece.type === "currency");
  return part?.value ?? currency;
}

/**
 * The locale whose digit grouping a business in this country reads: "en-IN" groups lakh and crore for India,
 * "en-US" or "en-AE" group in thousands. Plain "en" when the country is unknown.
 */
export function localeForCountry(country: string | null | undefined): string {
  const code = country?.trim().toUpperCase();
  if (!code || !/^[A-Z]{2}$/.test(code)) return "en";
  try {
    return Intl.getCanonicalLocales(`en-${code}`)[0] ?? "en";
  } catch {
    return "en";
  }
}
